/**
 * 배포 다이얼로그 — 이번 배포에 쓰이는 현장 설정 파일과 그 파일을 읽는 서비스.
 * 서비스 선택과 나란히 보여 "무엇을 어떤 설정으로 올리는지" 를 한 화면에서 확인한다.
 */
import { SITE_BATCH_SERVICE_CATALOG } from '../../../models/SiteBatch';
import type { SiteConfigFile } from '../../../services/SiteConfigService';
import type { DeployTabType } from '../types';
import type { SelectionMap } from './useDeployServiceSelection';

/**
 * 설정 파일 → 그 파일을 읽는 배포 서비스(appname). '*' 는 사내 compose 라 전체 서비스,
 * 빈 배열은 이 배포 목록 밖(영상처리서버는 노드에 따로 올린다).
 * 파일 목록 원본은 백엔드 deployment/usecase/siteConfigUseCase.go siteConfigFiles.
 */
const CONFIG_FILE_SERVICES: Record<string, string[] | '*'> = {
  'router.env': ['smartparking-router'],
  'lights.toml': ['lights'],
  'lights.env': ['lights'],
  'induce.env': ['smartparking-induce', 'smartparking-induce-worker'],
  'rtspback.env': ['smartparking-rtsp-back'],
  'ht-location.env': ['ht-location'],
  'camera-server.env': [],
  'company.env': '*',
};

/** 그 서비스 전용 설정 파일 이름 — 사내 compose 처럼 전체가 읽는 파일은 뺀다. */
export function serviceConfigNames(appname: string): string[] {
  return Object.entries(CONFIG_FILE_SERVICES)
    .filter(([, apps]) => apps !== '*' && apps.includes(appname))
    .map(([name]) => name);
}

/** 어느 한 서비스 전용이 아닌 파일 — 사내 compose(전체)·영상처리서버(배포 목록 밖)·모르는 파일. */
export function isSharedConfig(name: string): boolean {
  const apps = CONFIG_FILE_SERVICES[name];
  return apps === undefined || apps === '*' || apps.length === 0;
}

export interface DeployConfigFile extends SiteConfigFile {
  /** 이 파일을 읽는 서비스 라벨 — 비면 이 배포 목록 밖 */
  services: string[];
  /** 이번 배포에 들어가는(배포안함이 아닌) 서비스가 읽는 파일 */
  active: boolean;
}

const LABEL = new Map(SITE_BATCH_SERVICE_CATALOG.map((s) => [s.appname, s.label]));

/**
 * 배포 대상에 맞는 파일만, 이번 배포에 들어가는 것 먼저.
 * 현장 배포는 현장 통합서버 파일만, 사내 배포는 사내 compose 파일까지 본다.
 */
export function deployConfigFiles(
  files: SiteConfigFile[],
  target: DeployTabType,
  selection: SelectionMap,
): DeployConfigFile[] {
  const deploying = (appname: string) => selection.get(appname)?.mode !== 'skip';
  const out = files
    .filter((f) => target === 'company' || f.scope === 'site')
    .map((f): DeployConfigFile => {
      const mapped = CONFIG_FILE_SERVICES[f.name] ?? [];
      const appnames = mapped === '*' ? SITE_BATCH_SERVICE_CATALOG.map((s) => s.appname) : mapped;
      return {
        ...f,
        services: mapped === '*' ? ['전체 서비스'] : appnames.map((a) => LABEL.get(a) ?? a),
        active: appnames.some(deploying),
      };
    });
  return out.sort((a, b) => Number(b.active) - Number(a.active));
}
